import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';

import { FuseConfigService } from '@fuse/services/config.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';

import { navigation } from 'app/navigation/navigation';
import { AuthService } from '@app/core/services/auth.service';
import { Router } from '@angular/router';
import { User, USER_ROLE_OPTIONS } from '@app/core/models/user.model';

import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { locale as en } from './i18n/en';
import { locale as zh } from './i18n/zh';
import { ChangePasswordComponent } from '@app/main/auth/change-password/change-password.component';
import { EditProfileComponent } from '@app/main/auth/edit-profile/edit-profile.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'toolbar',
    templateUrl: './toolbar.component.html',
    styleUrls: ['./toolbar.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class ToolbarComponent implements OnInit, OnDestroy {
    readonly USER_ROLE_OPTIONS = USER_ROLE_OPTIONS;

    horizontalNavbar: boolean;
    rightNavbar: boolean;
    hiddenNavbar: boolean;
    languages: any;
    navigation: any;
    selectedLanguage: any;
    userStatusOptions: any[];
    currentUser: User;

    // Private
    private _unsubscribeAll: Subject<any>;

    /**
     * Constructor
     *
     * @param {FuseConfigService} _fuseConfigService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {TranslateService} _translateService
     */
    constructor(
        private _fuseConfigService: FuseConfigService,
        private _fuseSidebarService: FuseSidebarService,
        private matDialog: MatDialog,
        private translateService: TranslateService,
        private fuseTranslationLoaderService: FuseTranslationLoaderService,
        private authService: AuthService,
        private router: Router
    ) {
        // Set the defaults
        this.userStatusOptions = [
            {
                title: 'Online',
                icon: 'icon-checkbox-marked-circle',
                color: '#4CAF50'
            },
            {
                title: 'Away',
                icon: 'icon-clock',
                color: '#FFC107'
            },
            {
                title: 'Do not Disturb',
                icon: 'icon-minus-circle',
                color: '#F44336'
            },
            {
                title: 'Invisible',
                icon: 'icon-checkbox-blank-circle-outline',
                color: '#BDBDBD'
            },
            {
                title: 'Offline',
                icon: 'icon-checkbox-blank-circle-outline',
                color: '#616161'
            }
        ];

        this.languages = [
            {
                id: 'en',
                title: 'English',
                flag: 'us'
            },
            {
                id: 'zh',
                title: '中文',
                flag: 'zh'
            }
        ];

        this.navigation = navigation;

        this.fuseTranslationLoaderService.loadTranslations(en, zh);

        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to the config changes
        this._fuseConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((settings) => {
                this.horizontalNavbar = settings.layout.navbar.position === 'top';
                this.rightNavbar = settings.layout.navbar.position === 'right';
                this.hiddenNavbar = settings.layout.navbar.hidden === true;
            });

        this.translateService.onLangChange
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => this.selectedLanguage = _.find(this.languages, { id: this.translateService.currentLang }));

        // Set the selected language from default languages
        this.selectedLanguage = _.find(this.languages, { id: this.translateService.currentLang });

        this.authService.currentUser$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(
                data => (this.currentUser = data)
            );
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }

    /**
     * Search
     *
     * @param value
     */
    search(value): void {
        // Do your search here...
        console.log(value);
    }

    /**
     * Set the language
     *
     * @param lang
     */
    setLanguage(lang): void {
        // Set the selected language for the toolbar
        this.selectedLanguage = lang;

        // Use the selected language for translations
        this.translateService.setDefaultLang('en');
        this.translateService.setDefaultLang(lang.id);
        this.translateService.use(lang.id);
        localStorage['lang'] = lang.id;
    }

    editProfile(): void {
        this.matDialog.open(EditProfileComponent, {
            panelClass: 'edit-profile',
            data: this.currentUser
        });
    }

    changePassword(): void {
        this.matDialog.open(ChangePasswordComponent, {
            panelClass: 'change-password'
        });
    }

    logout(): void {
        this.authService.purgeAuth();
        this.router.navigate(['/auth/login'], { replaceUrl: true });
    }

    viewCheckoutPage(): void {
        const protocol = window.location.protocol;
        const host = window.location.host;
        const url = `${protocol}//${host}`;

        window.open(url, '_blank');
    }
}
